<style lang="scss" scoped>
.tabs {
    display: flex;
    flex-flow: row;
    margin: 0 -7.5px;
    @media only screen and (max-width: 767px) {
        flex-wrap: wrap;
        margin: 16px -7.5px 0px -7.5px;
    }
    .tab {
        color: var(--primary-dark);
        border: 1px solid var(--primary-dark);
        cursor: pointer;
        padding: 5px 15px;
        margin: 0 7.5px;
        &.green {
            color: var(--success);
            border: 1px solid var(--success);
            &.active,
            &:hover {
                background: var(--success);
                color: #fff;
            }
        }
        @media only screen and (max-width: 767px) {
            width: 50%;
            width: calc(50% - 16px);
            margin: 0 7.5px 16px 7.5px;
        }
        &.active,
        &:hover {
            background: var(--primary-dark);
            color: #fff;
        }
    }
}

::v-deep .table-container {
    max-height: calc(100vh - 220px);
    max-height: calc(var(--vh, 1vh) * 100 - 220px);
}
</style>

<template>
    <div v-if="currentUser.permissions.includes('user.read') || (currentUser.psychologist && currentUser.psychologist.status === 'approved')">
        <el-table class="px-md-44 py-16 py-md-32" :newRowCondition="newRowCondition" :loading="loading" :rows="patients" :structure="structure" :searchable="searchable" :query-parameters="{ filter: filter }" :meta="tableMeta" @meta-changed="(meta) => { tableMeta = meta; getFilteredPatients()  }" :server-side-sorting="true" detail="admin-show-patient">
            <template slot="header">
                <div class="col-auto ml-auto">
                    <router-link :to="{ name: 'admin-add-patient' }" class="btn btn-gold btn-sm d-none">
                        + {{ $t('pages.patients.patient') }}
                    </router-link>
                </div>
                <div class="col-auto ml-auto">
                    <div class="tabs d-flex flex-wrap">
                        <div class="tab green" :class="{ active: filter === 'new' }" @click="filter = 'new'; tableMeta.page = 1; getFilteredPatients()">{{  $t('table.patients.new') }} {{ `(${statistics.new_count})`}}</div>
                        <div class="tab" :class="{ active: filter === 'active' }" @click="filter = 'active'; tableMeta.page = 1; getFilteredPatients()">{{ $t('table.patients.active') }} {{ `(${statistics.active_count})`}}</div>
                        <div class="tab" :class="{ active: filter === 'trial' }" @click="filter = 'trial'; tableMeta.page = 1; getFilteredPatients()">{{ $t('table.patients.trial') }}</div>
                        <div class="tab" :class="{ active: filter === 'inactive' }" @click="filter = 'inactive'; tableMeta.page = 1; getFilteredPatients()">{{ $t('table.patients.plan_ended') }}</div>
                        <div class="tab" :class="{ active: filter === 'invited' }" @click="filter = 'invited'; tableMeta.page = 1; getFilteredPatients()">{{ $t('table.patients.invited') }}</div>
                        <div class="tab" :class="{ active: filter === 'all' }" @click="filter = 'all'; tableMeta.page = 1; getFilteredPatients()">{{ $t('table.patients.all') }}</div>
                        <router-link v-if="currentUser.permissions.includes('user.invite') || (currentUser.psychologist && currentUser.psychologist.status === 'approved')" :to="{ name: 'admin-add-patient' }" class="btn btn-gold ml-32 d-none d-md-block">
                            {{ $t('pages.dashboard.add_patient') }}
                        </router-link>
                    </div>
                </div>
            </template>
        </el-table>
    </div>
    <div v-else class="container" style="height: calc(100% - 68px)">
        <div class="row h-100">
            <div class="col-auto m-auto">
                <image-no-access height="300"></image-no-access>
                <h4 class="text-primary-dark fw-b text-center mt-32">{{ $t('errors.no_admin_access') }}</h4>
                <p class="text-center">{{ $t('errors.no_admin_access_desc') }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { ElTable } from '@/components/tables'
export default {
    name: 'Page-patients-index',

    components: { ElTable },

    data() {
        return {
            searchable: ['first_name', 'last_name', 'email'],
            filter: 'all',
            loading: false,
            tableMeta: { page: 1 },
            newRowCondition: function (d) { return d.viewed_at === null },
            structure: [
                { visible: true, label: this.$t('table.patients.last_followup'), type: 'follow_up_date', key: 'followed_up_at', sortable: true, size: 'small', class: ['fw-b'] },
                { visible: true, label: this.$t('table.patients.name'), type: 'user', sortable: true, key: 'name' },
                { visible: true, label: this.$t('table.patients.email'), type: 'text', key: 'email', sortable: true },
                { visible: true, label: this.$t('table.patients.registered'), type: 'date', key: 'registered', sortable: true, size: 'small' },
                { visible: true, label: this.$t('table.patients.started_at'), type: 'date', key: 'started_at', sortable: true, size: 'xsmall' },
                { visible: true, label: this.$t('table.patients.paid'), type: 'paid', key: 'paid', sortable: true, size: 'xsmall' },
                { visible: true, label: this.$t('table.patients.therapy'), type: 'followup', key: 'labels', sortable: true, size: 'xsmall' },
                { visible: true, label: this.$t('table.patients.progress'), type: 'text', key: 'progress', sortable: true, size: 'xsmall' },
                { visible: true, label: this.$t('table.patients.sleep_efficiency'), type: 'text', key: 'sleep_efficiency', sortable: true, size: 'xxsmall' },
                { visible: true, label: this.$t('table.patients.schedule'), type: 'text', key: 'schedule', sortable: true, size: 'xsmall' },
                { visible: true, label: this.$t('table.patients.medication'), type: 'boolean', key: 'has_medication', sortable: true, size: 'xsmall' },
                { visible: true, label: this.$t('table.patients.psychologist'), type: 'text', key: 'psychologist', sortable: true, size: 'small' },
                { visible: true, label: this.$t('table.patients.labels'), type: 'labels', key: 'labels', sortable: true, size: 'normal' },
                { visible: true, label: this.$t('table.patients.referral_code'), type: 'text', key: 'referral_code', sortable: false, size: 'small' },
                { visible: false, label: this.$t('table.patients.leeftijd'), type: 'number', key: 'age', sortable: true, size: 'xsmall' },
            ],
            statistics: { new_count: 0, active_count: 0 }
        }
    },

    mounted() {
        this.getFilteredPatients();
        if (this.$route.query.filter) this.filter = this.$route.query.filter
    },

    methods: {
        getFilteredPatients() {
            this.loading = true;

            this.tableMeta.status = this.filter;

            this.$store.dispatch('patients/index', this.tableMeta).then((p) => {
                this.tableMeta = { ...this.tableMeta, ...p.meta };
                this.loading = false;
            })

            this.$store.dispatch('patients/statistics').then((statistics) => {
                this.statistics = statistics
            })
        }
    },

    computed: {
        currentUser() {
            return this.$store.getters['user/currentUser']
        },
        patients() {
            return this.$store.getters['patients/all'].map(obj => ({ ...obj, selected: false }))
        },
    },
}
</script>
